import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Zap, Cable, Wrench, Building2, Lightbulb, Check } from 'lucide-react';
import { fetchCollection } from '../lib/cms';
import AnimatedNumber from '../components/AnimatedNumber';

const iconMap = {
  Zap: Zap,
  Cable: Cable,
  Wrench: Wrench,
  Building2: Building2,
  Lightbulb: Lightbulb,
};

export default function Home() {
  const [services, setServices] = useState([]);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    fetchCollection('services_v2').then(setServices);
    
    // Set SEO / Marketing tags
    document.title = "L1";
    
    const metaTags = [
      { name: 'description', value: '' },
      { property: 'og:title', value: 'L1' },
      { property: 'og:description', value: '' },
      { property: 'og:image', value: '' }
    ];

    metaTags.forEach(tag => {
      const selector = tag.name 
        ? `meta[name="${tag.name}"]` 
        : `meta[property="${tag.property}"]`;
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        if (tag.name) element.setAttribute('name', tag.name);
        if (tag.property) element.setAttribute('property', tag.property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', tag.value);
    });
  }, []);

  const displayedServices = showAll ? services : services.slice(0, 3);

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] md:min-h-[70vh] lg:min-h-[60vh] flex items-center overflow-hidden pt-20 pb-12 md:pt-40 md:pb-32 lg:pt-32 lg:pb-24">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/c790a797-f8bb-43f8-b6f5-1dd422b46a5f/25c5de50-e4a8-4546-9e89-124f3b9cc05f.jpg"
            alt="Elektriker i arbejde"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl text-left"
          >
            <div className="text-white text-xs font-semibold uppercase tracking-widest mb-6">
              Autoriseret El-installatør
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6 tracking-tight uppercase">
              Professionelle el-løsninger til <span className="text-[#C1121F]">private og erhverv</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-10 leading-relaxed">
              L1 leverer moderne, sikre og effektive el-installationer med fokus på kvalitet, driftssikkerhed og professionelt håndværk.
            </p>
            <div className="flex justify-start">
              <Link
                to="/kontakt"
                className="px-8 py-4 bg-[#C1121F] text-white font-bold uppercase tracking-wider rounded-none hover:bg-[#A00F1A] transition-all flex items-center justify-center group"
              >
                Kontakt os
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Intro Section */}
      <section id="services" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-end mb-20">
            <div>
              <h2 className="text-sm font-semibold text-[#C1121F] uppercase tracking-[0.3em] mb-4">Hvad vi tilbyder</h2>
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight uppercase">
                Komplette løsninger indenfor el og teknik
              </h3>
            </div>
            <div>
              <p className="text-lg text-gray-600 leading-relaxed">
                Hos L1 tilbyder vi professionelle el-løsninger indenfor installationer, kabelføring, service og entrepriseopgaver. Vi kombinerer teknisk ekspertise med moderne metoder.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayedServices.map((service, index) => {
              const Icon = iconMap[service.icon_name_text] || Zap;
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/vi-tilbyder/${service.slug_text.da}`}
                    className="group block p-8 bg-white shadow-xl border border-gray-100 rounded-none transition-all h-full relative overflow-hidden"
                  >
                    <div className="absolute top-0 right-0 p-4 opacity-5 transition-opacity">
                      <Icon className="w-24 h-24 text-black" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-4 uppercase tracking-tight">{service.title.da}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed mb-8 line-clamp-3">
                      {service.description.da.replace(/<[^>]*>/g, '')}
                    </p>
                    <div className="flex items-center text-black font-bold text-xs uppercase tracking-widest">
                      Læs mere
                      <ArrowRight className="ml-2 w-4 h-4 transition-transform" />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          {services.length > 3 && (
            <div className="mt-16 flex justify-center">
              <button
                onClick={() => setShowAll(!showAll)}
                className="px-10 py-4 border-2 border-[#C1121F] text-[#C1121F] font-bold uppercase tracking-widest hover:bg-[#C1121F] hover:text-white transition-all rounded-none"
              >
                {showAll ? 'Vis færre' : 'Se alle ydelser'}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* About Section */}
      <section className="bg-[#0F0F0F] overflow-hidden">
        <div className="flex flex-col md:flex-row">
          <div className="w-full md:w-1/2 min-h-[400px] md:min-h-[600px] relative">
            <img
              src="https://assets.auxodev.dk/assets/c790a797-f8bb-43f8-b6f5-1dd422b46a5f/9c348c98-7a96-46cd-8dee-80f18c09e318.jpg"
              alt="L1 El-installatør arbejde"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div className="w-full md:w-1/2 p-8 md:p-16 lg:p-24 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-8 uppercase leading-tight">
                L1 – Kvalitet, sikkerhed og professionelt håndværk
              </h3>
              <p className="text-[#f1f1f1] text-lg leading-relaxed mb-10">
                L1 er en moderne el-virksomhed med fokus på kvalitet, pålidelighed og professionelle løsninger. Vi arbejder med både private, erhverv og entrepriser, hvor vores mål altid er at levere stabile installationer og høj service.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  'God kommunikation',
                  'Høj kvalitet',
                  'Effektive løsninger',
                  'Professionelt samarbejde'
                ].map((value, i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <Check className="w-6 h-6 text-white flex-shrink-0" />
                    <span className="text-[#f1f1f1] font-medium uppercase text-xs tracking-wider">{value}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-12">
                <Link
                  to="/om-os"
                  className="inline-flex items-center text-white font-bold uppercase tracking-widest text-xs hover:text-[#C1121F] transition-colors group"
                >
                  Læs mere om vores vision
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-24 bg-black relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#C1121F] to-transparent opacity-30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-12 text-center">
            <div>
              <div className="text-5xl font-bold text-white mb-2">
                <AnimatedNumber value="100" suffix="%" />
              </div>
              <div className="text-[#C1121F] font-semibold uppercase tracking-widest text-xs">Kvalitetsgaranti</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-white mb-2">24/7</div>
              <div className="text-[#C1121F] font-semibold uppercase tracking-widest text-xs">Service & Support</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-white mb-2">Autoriseret</div>
              <div className="text-[#C1121F] font-semibold uppercase tracking-widest text-xs">Sikkerhed først</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white">
        <div className="bg-gray-50 border-y border-gray-100 p-12 md:p-24 text-center relative overflow-hidden">
          {/* Dekorativt rødt element i højre side */}
          <div className="absolute top-0 right-0 w-1/2 h-full bg-[#C1121F] opacity-20 transform -skew-x-12 translate-x-1/3 pointer-events-none" />
          
          <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none">
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#C1121F] blur-[120px] rounded-full" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-[#C1121F] blur-[120px] rounded-full" />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 uppercase">
              Skal vi hjælpe dig med dit næste projekt?
            </h2>
            <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
              Kontakt os i dag for en uforpligtende snak om dine el-installationer. Vi står klar med professionel rådgivning.
            </p>
            <Link
              to="/kontakt"
              className="inline-flex px-10 py-5 bg-[#C1121F] text-white font-bold uppercase tracking-wider rounded-none hover:bg-[#A00F1A] transition-all transform hover:scale-105"
            >
              Få et uforpligtende tilbud
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
